/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useRef, useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createBrowserRouter, Outlet, RouterProvider, Navigate, useOutletContext } from 'react-router-dom';
import { ThemeProvider } from '@material-tailwind/react';
import { ClerkProvider, SignedOut, useAuth, useOrganization, useUser } from '@clerk/clerk-react';
import Start from './Pages/Start.jsx';
import Call from './Pages/Call.jsx';
import Report from './Pages/Report.jsx';
import AuthedLayout from './AuthedLayout.jsx';
import SignIn from './Pages/SignIn.jsx';
import Login from './Pages/Login.jsx';
import Dashboard from './NewDashboard/index.jsx';
import VerifyEmail from './Components/verifyEmail.jsx';
import Followup from './Pages/Followup.jsx';
// import RedirectToCall from './Components/RedirectToCall.jsx';
import WaitingForCall from './Components/WaitingForCall.jsx';
import Callback from './Pages/callback.tsx';
import Navbar from './Components/Navbar.jsx';
import Pricing from './Pages/Pricing.jsx';
import PaymentSuccess from './Pages/PaymentSuccess.jsx';
import NotFound from './Pages/NotFound.jsx';
import { AxiosProvider } from './Components/AxiosProvider.jsx';
import Loading from './Components/Loading.jsx';
import SubscriptionGuard from './Components/SubscriptionGuard.jsx';
import { Sidebar } from './Dashboard/Container.jsx';
import { RootContext } from './rootContext.jsx';
import { RootContextProvider } from './RootContextProvider.jsx';
import OrganizationManagement from './Pages/OrganizationManagement.jsx';
import OrganizationInvite from './Pages/OrganizationInvite.jsx';
import { useClerkLocalization } from './hooks/useClerkLocalization.js';
import { SyncActiveOrganizationFromURLToSession } from './hooks/setOrganisation.jsx';
import TawkToChat from './Components/TawkToChat.jsx';
import { useReportIds } from './hooks/useReportIds.js';
import Goals from './Pages/Goal.jsx';
import { Toaster } from './Components/ui/toaster.tsx';
import { Toaster as Sonner } from './Components/ui/sonner.tsx';
import DashboardAnalytics from './Pages/DashboardAnalytics.jsx';
import HealthJournal from './Pages/HealthJournal.jsx';
import IntakeProcess from './Components/IntakeComponents/IntakeProcess.tsx';
import InitialAssessmentReport from './Components/IntakeComponents/InitialAssessmentReport.tsx';
import WellnessPlanningReport from './Components/IntakeComponents/HealthAssessmentReport.tsx';
import ActionPlanReport from './Components/IntakeComponents/ActionPlanReport.tsx';
import StartSession from './Components/IntakeComponents/StartSession.tsx';
import i18n from './i18n.js';

const RootLayout = () => {
  const { user } = useUser();
  const { organization } = useOrganization();
  const { setIsSidebarOpen } = useContext(RootContext);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const navbarRef = useRef(null);

  useEffect(() => {
    if (navbarRef.current) {
      setNavbarHeight(navbarRef.current.offsetHeight);
    }
  }, []);

  // Memoize organization language to prevent unnecessary re-renders
  const organizationLanguage = useMemo(() => {
    return organization?.publicMetadata?.language;
  }, [organization?.publicMetadata?.language]);

  const organizationLanguageUI = useMemo(() => {
    return organization?.publicMetadata?.languageUI;
  }, [organization?.publicMetadata?.languageUI]);

  const organizationId = useMemo(() => {
    return organization?.id;
  }, [organization?.id]);

  const userPreferredLanguage = useMemo(() => {
    return user?.unsafeMetadata?.preferredLanguage;
  }, [user?.unsafeMetadata?.preferredLanguage]);

  useEffect(() => {
    if (!userPreferredLanguage && organizationLanguage && organizationLanguageUI === false) {
      i18n.changeLanguage(organizationLanguage);
      localStorage.setItem('preferredLanguage', organizationLanguage);

      user?.update({
        unsafeMetadata: {
          ...user?.unsafeMetadata,
          preferredLanguage: organizationLanguage,
          organizationId,
        },
      });
    }
  }, [organizationLanguage, organizationLanguageUI, userPreferredLanguage, organizationId]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, [setIsSidebarOpen]);

  return (
    <div className="bg-white">
      <Navbar ref={navbarRef} user={user} toggleSidebar={toggleSidebar} />
      <Outlet context={{ navbarHeight }} />
    </div>
  );
};

const Container = () => {
  const { navbarHeight } = useOutletContext() || { navbarHeight: 0 };
  return (
    <div className="bg-white" style={{ marginTop: `${navbarHeight}px`, height: `calc(100vh - ${navbarHeight}px)` }}>
      <main className="overflow-hidden min-h-screen">
        <SignedOut>
          <Navigate to={'/landing'} replace />
        </SignedOut>
        <Outlet />
      </main>
    </div>
  );
};

const DashboardLayout = () => {
  const { isSidebarOpen, language } = useContext(RootContext);
  const { isLoaded, userId } = useAuth();
  const { reportIds, isLoading } = useReportIds();
  const { location } = window;

  // Memoize the Clerk ticket check to prevent unnecessary re-renders
  const hasClerkTicket = useMemo(() => {
    return location.search.includes('__clerk_ticket');
  }, [location.search]);

  // If we have a Clerk ticket, wait longer before deciding user is not authenticated
  // This gives Clerk more time to process the ticket
  if (!isLoaded || isLoading || (hasClerkTicket && !userId)) return null;
  if (!userId) return <Navigate to="/signin" replace />;

  return (
    <div className="flex flex-col h-screen relative bg-[#F5F8F6]">
      <div className="flex flex-1 overflow-hidden pt-10">
        <Sidebar reportId={reportIds.INTAKE} isOpen={isSidebarOpen} language={language} />
        <main className="flex-1 overflow-auto p-4 bg-[#F5F8F6]">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const AppContent = () => {
  const { isLoaded, isSignedIn } = useAuth();

  // Memoize the theme object to prevent unnecessary re-renders
  const theme = useMemo(
    () => ({
      button: {
        styles: {
          base: {
            initial: {
              textTransform: 'none',
            },
          },
          sizes: {
            sm: {
              fontSize: 'text-base',
            },
            md: {
              fontSize: 'text-xl',
            },
          },
        },
      },
      input: {
        defaultProps: {
          size: 'lg',
        },
      },
    }),
    [],
  );

  // Memoize ProtectedRoute to prevent recreating it on every render
  const ProtectedRoute = useMemo(() => {
    const Component = ({ children }) => {
      if (!isSignedIn) {
        return <Navigate to="/signin" replace />;
      }
      return <AxiosProvider>{children}</AxiosProvider>;
    };

    Component.propTypes = {
      children: PropTypes.node.isRequired,
    };

    return Component;
  }, [isSignedIn]);

  if (!isLoaded) {
    return <Loading className="h-screen" />;
  }

  const routerConfig = createBrowserRouter([
    {
      path: '/',
      element: <RootLayout />,
      children: [
        {
          path: '/auth',
          element: (
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          ),
          children: [
            {
              path: 'dashboard',
              element: (
                <SubscriptionGuard>
                  <Dashboard />
                </SubscriptionGuard>
              ),
            },
            {
              path: 'report/:reportId',
              element: (
                <ProtectedRoute>
                  <IntakeProcess />
                </ProtectedRoute>
              ),
              children: [
                {
                  path: 'intake',
                  element: <Report />,
                },
                {
                  path: 'intake1',
                  element: <InitialAssessmentReport />,
                },
                {
                  path: 'intake2',
                  element: <WellnessPlanningReport />,
                },
                {
                  path: 'intake3',
                  element: <ActionPlanReport />,
                },
              ],
            },
            {
              path: 'followup',
              element: (
                <SubscriptionGuard>
                  <Followup />
                </SubscriptionGuard>
              ),
            },
            {
              path: 'health-journal',
              element: (
                // <SubscriptionGuard>
                <HealthJournal />
                // </SubscriptionGuard>
              ),
            },
            {
              path: 'goals',
              element: <Goals />,
            },
            {
              path: 'dashboard-analytics',
              element: <DashboardAnalytics />,
            },
            {
              path: 'start-session',
              element: <StartSession />,
            },
          ],
        },
        {
          path: '/landing',
          element: <Callback />,
        },
        {
          path: '/pricing',
          element: <Pricing />,
        },
        {
          path: '/',
          element: <Container />,
          children: [
            {
              path: 'start',
              element: <Start />,
            },
            {
              path: 'verify-email',
              element: <VerifyEmail />,
            },
            {
              path: 'report/:reportId',
              element: <Report />,
            },
            {
              path: 'organization-management',
              element: <OrganizationManagement />,
            },
            {
              path: 'signin',
              element: <SignIn />,
            },
            {
              path: 'login',
              element: <Login />,
            },
            {
              path: 'start-session',
              element: (
                <ProtectedRoute>
                  <StartSession />
                </ProtectedRoute>
              ),
            },
            {
              element: <AuthedLayout />,
              path: 'auth',
              children: [
                { path: 'call', element: <Call /> },
                {
                  path: 'start-report',
                  element: (
                    <ProtectedRoute>
                      <IntakeProcess />
                    </ProtectedRoute>
                  ),
                  children: [
                    {
                      path: 'intake1',
                      element: <InitialAssessmentReport />,
                    },
                    {
                      path: 'intake2',
                      element: <WellnessPlanningReport />,
                    },
                    {
                      path: 'intake3',
                      element: <ActionPlanReport />,
                    },
                  ],
                },
                {
                  path: 'waiting-for-call',
                  element: (
                    <ProtectedRoute>
                      <WaitingForCall />
                    </ProtectedRoute>
                  ),
                },
              ],
            },
          ],
        },
        {
          path: 'payments/success',
          element: (
            <ProtectedRoute>
              <PaymentSuccess />,
            </ProtectedRoute>
          ),
        },
        {
          path: '/invite/:slug',
          element: <OrganizationInvite />,
        },
        {
          path: '*',
          element: <NotFound />,
        },
      ],
    },
  ]);

  return (
    <ThemeProvider value={theme}>
      <RouterProvider router={routerConfig} />
    </ThemeProvider>
  );
};

const App = () => {
  const localization = useClerkLocalization();
  return (
    <ClerkProvider publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY} renderLoader={() => null} afterSignOutUrl="/" localization={localization}>
      <Toaster />
      <Sonner />
      <RootContextProvider>
        <SyncActiveOrganizationFromURLToSession />
        <AppContent />
        <TawkToChat />
      </RootContextProvider>
    </ClerkProvider>
  );
};

export default App;
